import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import {
  Briefcase, Calendar, DollarSign, MapPin, Clock, LogOut,
  CheckCircle, Play, HardHat, Navigation, FileText,
  AlertTriangle, Wrench, Send, ChevronDown, ChevronUp
} from "lucide-react";

const STATUS_COLORS = {
  assigned: "bg-blue-500/20 text-blue-400",
  accepted: "bg-green-500/20 text-green-400",
  needs_review: "bg-yellow-500/20 text-yellow-400",
  approved: "bg-emerald-500/20 text-emerald-400",
  in_progress: "bg-orange-500/20 text-orange-400",
  completed: "bg-green-600/20 text-green-300",
  cancelled: "bg-red-500/20 text-red-400"
};

const STATUS_LABELS = {
  assigned: "Assigned",
  accepted: "Accepted",
  needs_review: "Needs Review",
  approved: "Approved",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled"
};

const EmployeePortalPage = () => {
  const navigate = useNavigate();
  const [emp, setEmp] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [earnings, setEarnings] = useState({ total_earned: 0, jobs: [] });
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("jobs");
  const [checkInLocation, setCheckInLocation] = useState("");
  const [checkInJobId, setCheckInJobId] = useState("");
  const [expandedJob, setExpandedJob] = useState(null);
  const [noteForm, setNoteForm] = useState({ notes: "", materials_needed: "", additional_work: "", issues_found: "" });
  const [submittingNotes, setSubmittingNotes] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [meRes, jobsRes, schedRes, earnRes] = await Promise.all([
        axios.get(`${API}/employee/me`, { withCredentials: true }),
        axios.get(`${API}/employee/my-jobs`, { withCredentials: true }),
        axios.get(`${API}/employee/my-schedule`, { withCredentials: true }),
        axios.get(`${API}/employee/my-earnings`, { withCredentials: true })
      ]);
      setEmp(meRes.data);
      setJobs(jobsRes.data);
      setSchedule(schedRes.data);
      setEarnings(earnRes.data);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        navigate("/employee/login");
        return;
      }
    } finally { setLoading(false); }
  };

  const handleAcceptJob = async (jobId) => {
    try {
      await axios.patch(`${API}/employee/jobs/${jobId}/status`, { status: "accepted" }, { withCredentials: true });
      toast.success("Job accepted!");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to accept"); }
  };

  const handleStartJob = async (jobId) => {
    try {
      await axios.patch(`${API}/employee/jobs/${jobId}/status`, { status: "in_progress" }, { withCredentials: true });
      toast.success("Job started!");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start"); }
  };

  const handleCompleteJob = async (jobId) => {
    try {
      await axios.patch(`${API}/employee/jobs/${jobId}/status`, { status: "completed" }, { withCredentials: true });
      toast.success("Job completed!");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to complete"); }
  };

  const handleSubmitNotes = async (jobId) => {
    if (!noteForm.notes && !noteForm.materials_needed && !noteForm.additional_work && !noteForm.issues_found) {
      toast.error("Add at least one note or detail"); return;
    }
    setSubmittingNotes(true);
    try {
      await axios.post(`${API}/employee/jobs/${jobId}/notes`, noteForm, { withCredentials: true });
      toast.success("Update submitted — admin will review");
      setNoteForm({ notes: "", materials_needed: "", additional_work: "", issues_found: "" });
      setExpandedJob(null);
      fetchAll();
    } catch (e) { toast.error("Failed to submit update"); }
    finally { setSubmittingNotes(false); }
  };

  const handleCheckIn = async () => {
    if (!checkInLocation) { toast.error("Enter your location"); return; }
    try {
      await axios.post(`${API}/employee/check-in`, { location: checkInLocation, job_id: checkInJobId }, { withCredentials: true });
      toast.success("Checked in!");
      setCheckInLocation("");
    } catch (e) { toast.error("Check-in failed"); }
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch (e) { /* ignore */ }
    navigate("/employee/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#FF6B35]"></div>
      </div>
    );
  }

  const todayStr = new Date().toISOString().split("T")[0];
  const todayJobs = jobs.filter(j => j.scheduled_date === todayStr);
  const pendingJobs = jobs.filter(j => j.status === "assigned");
  const activeJobs = jobs.filter(j => ["accepted", "in_progress", "needs_review", "approved"].includes(j.status));

  return (
    <div className="min-h-screen bg-[#0B0C10]" data-testid="employee-portal">
      {/* Header */}
      <div className="bg-[#15161C] border-b border-[#2A2B32] px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-white">{emp?.name || "Team Member"}</h1>
            <p className="text-[#A0AAB2] text-xs capitalize">{emp?.role || "technician"} — New Leaf PMC</p>
          </div>
          <button onClick={handleLogout} className="p-2 text-[#A0AAB2] hover:text-white" data-testid="emp-logout">
            <LogOut size={20} />
          </button>
        </div>
      </div>

      {/* Check-In Bar */}
      <div className="bg-[#1F2027] border-b border-[#2A2B32] px-4 py-3">
        <div className="max-w-3xl mx-auto">
          <p className="text-[#A0AAB2] text-xs mb-2 font-medium">Where are you working today?</p>
          <div className="flex space-x-2">
            <select value={checkInJobId} onChange={e => setCheckInJobId(e.target.value)}
              className="flex-1 bg-[#15161C] border border-[#2A2B32] rounded-lg px-3 py-2.5 text-white text-sm" data-testid="checkin-job">
              <option value="">Select job</option>
              {jobs.filter(j => j.status !== "completed" && j.status !== "cancelled").map(j => (
                <option key={j.id} value={j.id}>{j.title} — {j.address || "No address"}</option>
              ))}
            </select>
            <input type="text" value={checkInLocation} onChange={e => setCheckInLocation(e.target.value)}
              placeholder="Address / Location" data-testid="checkin-location"
              className="flex-1 bg-[#15161C] border border-[#2A2B32] rounded-lg px-3 py-2.5 text-white text-sm" />
            <button onClick={handleCheckIn} className="px-5 py-2.5 gradient-btn rounded-lg text-sm font-semibold flex items-center space-x-1" data-testid="checkin-btn">
              <Navigation size={16} /><span className="hidden sm:inline">Check In</span>
            </button>
          </div>
        </div>
      </div>

      {/* Nav Tabs */}
      <div className="bg-[#15161C] border-b border-[#2A2B32] px-4">
        <div className="max-w-3xl mx-auto flex space-x-1 py-2">
          {[
            { id: "jobs", label: "My Jobs", icon: Briefcase, badge: pendingJobs.length },
            { id: "schedule", label: "Schedule", icon: Calendar, badge: 0 },
            { id: "earnings", label: "Earnings", icon: DollarSign, badge: 0 }
          ].map(t => (
            <button key={t.id} onClick={() => setActiveView(t.id)} data-testid={`emp-tab-${t.id}`}
              className={`flex items-center space-x-2 px-5 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap ${activeView === t.id ? "bg-[#FF6B35] text-white" : "text-[#A0AAB2] hover:text-white"}`}>
              <t.icon size={18} />
              <span>{t.label}</span>
              {t.badge > 0 && <span className="bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 ml-1">{t.badge}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* JOBS VIEW */}
        {activeView === "jobs" && (
          <div className="space-y-4" data-testid="emp-jobs-view">
            {/* Pending Acceptance */}
            {pendingJobs.length > 0 && (
              <div>
                <h2 className="text-base font-bold text-white mb-3 flex items-center space-x-2">
                  <AlertTriangle size={16} className="text-yellow-400" />
                  <span>Awaiting Your Acceptance ({pendingJobs.length})</span>
                </h2>
                {pendingJobs.map(job => (
                  <div key={job.id} className="bg-[#15161C] border border-yellow-500/30 rounded-xl p-5 mb-3" data-testid={`emp-job-${job.id}`}>
                    <h3 className="text-white font-bold text-lg mb-1">{job.title}</h3>
                    <div className="flex flex-wrap gap-3 text-[#A0AAB2] text-sm mb-3">
                      {job.client_name && <span>{job.client_name}</span>}
                      {job.scheduled_date && <span className="flex items-center space-x-1"><Clock size={12} /><span>{job.scheduled_date} {job.scheduled_time}</span></span>}
                      {job.address && <span className="flex items-center space-x-1"><MapPin size={12} /><span>{job.address}</span></span>}
                    </div>
                    {job.description && <p className="text-[#A0AAB2] text-sm mb-3">{job.description}</p>}
                    <div className="flex items-center justify-between">
                      <span className="text-[#FF6B35] font-bold text-lg">${(job.total_price || 0).toLocaleString()}</span>
                      <button onClick={() => handleAcceptJob(job.id)} data-testid={`accept-job-${job.id}`}
                        className="gradient-btn px-8 py-3 rounded-xl font-bold text-base flex items-center space-x-2">
                        <CheckCircle size={20} /><span>Accept Job</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Active Jobs */}
            {activeJobs.length > 0 && (
              <div>
                <h2 className="text-base font-bold text-white mb-3">Active Jobs ({activeJobs.length})</h2>
                {activeJobs.map(job => {
                  const isExpanded = expandedJob === job.id;
                  return (
                    <div key={job.id} className="bg-[#15161C] border border-[#2A2B32] rounded-xl mb-3 overflow-hidden" data-testid={`emp-active-job-${job.id}`}>
                      <div className="p-5">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="text-white font-bold">{job.title}</h3>
                            <span className={`text-xs px-2.5 py-1 rounded-full inline-block mt-1 ${STATUS_COLORS[job.status] || ""}`}>
                              {STATUS_LABELS[job.status] || job.status}
                            </span>
                          </div>
                          <span className="text-[#FF6B35] font-bold">${(job.total_price || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex flex-wrap gap-3 text-[#A0AAB2] text-sm mb-3">
                          {job.client_name && <span>{job.client_name}</span>}
                          {job.scheduled_date && <span className="flex items-center space-x-1"><Clock size={12} /><span>{job.scheduled_date}</span></span>}
                          {job.address && <span className="flex items-center space-x-1"><MapPin size={12} /><span>{job.address}</span></span>}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-2">
                          {job.status === "accepted" && (
                            <button onClick={() => handleStartJob(job.id)} data-testid={`start-job-${job.id}`}
                              className="bg-orange-500/20 text-orange-400 px-5 py-2.5 rounded-xl text-sm font-bold flex items-center space-x-2 hover:bg-orange-500/30">
                              <Play size={16} /><span>Start Work</span>
                            </button>
                          )}
                          {(job.status === "in_progress" || job.status === "approved") && (
                            <button onClick={() => handleCompleteJob(job.id)} data-testid={`complete-job-${job.id}`}
                              className="bg-green-500/20 text-green-400 px-5 py-2.5 rounded-xl text-sm font-bold flex items-center space-x-2 hover:bg-green-500/30">
                              <CheckCircle size={16} /><span>Mark Complete</span>
                            </button>
                          )}
                          <button onClick={() => { setExpandedJob(isExpanded ? null : job.id); setNoteForm({ notes: "", materials_needed: "", additional_work: "", issues_found: "" }); }}
                            data-testid={`toggle-notes-${job.id}`}
                            className="bg-[#2A2B32] text-[#A0AAB2] px-5 py-2.5 rounded-xl text-sm font-bold flex items-center space-x-2 hover:text-white">
                            <FileText size={16} /><span>Add Notes</span>
                            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </button>
                        </div>
                      </div>

                      {/* Notes Form (Expanded) */}
                      {isExpanded && (
                        <div className="border-t border-[#2A2B32] p-5 bg-[#1A1B21]" data-testid={`notes-form-${job.id}`}>
                          <h4 className="text-white font-bold text-sm mb-3 flex items-center space-x-2">
                            <Wrench size={14} className="text-[#FF6B35]" />
                            <span>Job Notes / What's Needed</span>
                          </h4>
                          <div className="space-y-3">
                            <div>
                              <label className="text-[#A0AAB2] text-xs font-medium mb-1 block">Notes</label>
                              <textarea value={noteForm.notes} onChange={e => setNoteForm(p => ({...p, notes: e.target.value}))} rows={3}
                                placeholder="What did you find? What was done?"
                                className="w-full bg-[#15161C] border border-[#2A2B32] rounded-lg px-4 py-3 text-white text-sm resize-none" data-testid="note-text" />
                            </div>
                            <div>
                              <label className="text-[#A0AAB2] text-xs font-medium mb-1 block">Materials Needed</label>
                              <input type="text" value={noteForm.materials_needed} onChange={e => setNoteForm(p => ({...p, materials_needed: e.target.value}))}
                                placeholder="e.g. 2 gallons primer, 10ft drywall..."
                                className="w-full bg-[#15161C] border border-[#2A2B32] rounded-lg px-4 py-3 text-white text-sm" data-testid="note-materials" />
                            </div>
                            <div>
                              <label className="text-[#A0AAB2] text-xs font-medium mb-1 block">Additional Work Required</label>
                              <input type="text" value={noteForm.additional_work} onChange={e => setNoteForm(p => ({...p, additional_work: e.target.value}))}
                                placeholder="e.g. Needs electrical before drywall..."
                                className="w-full bg-[#15161C] border border-[#2A2B32] rounded-lg px-4 py-3 text-white text-sm" data-testid="note-additional" />
                            </div>
                            <div>
                              <label className="text-[#A0AAB2] text-xs font-medium mb-1 block">Issues Found</label>
                              <input type="text" value={noteForm.issues_found} onChange={e => setNoteForm(p => ({...p, issues_found: e.target.value}))}
                                placeholder="e.g. Water damage behind wall, mold..."
                                className="w-full bg-[#15161C] border border-[#2A2B32] rounded-lg px-4 py-3 text-white text-sm" data-testid="note-issues" />
                            </div>
                          </div>
                          <button onClick={() => handleSubmitNotes(job.id)} disabled={submittingNotes}
                            data-testid={`submit-notes-${job.id}`}
                            className="w-full gradient-btn py-3 rounded-xl font-bold text-sm mt-4 flex items-center justify-center space-x-2 disabled:opacity-50">
                            <Send size={16} /><span>{submittingNotes ? "Submitting..." : "Submit Job Update"}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Completed Jobs */}
            {jobs.filter(j => j.status === "completed").length > 0 && (
              <div>
                <h2 className="text-base font-bold text-white mb-3 text-green-400">Completed ({jobs.filter(j => j.status === "completed").length})</h2>
                {jobs.filter(j => j.status === "completed").map(job => (
                  <div key={job.id} className="bg-[#15161C] border border-green-500/20 rounded-xl p-4 mb-2 opacity-80">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-white font-medium">{job.title}</h3>
                        <p className="text-[#A0AAB2] text-xs">{job.client_name} — {job.scheduled_date}</p>
                      </div>
                      <CheckCircle size={20} className="text-green-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {jobs.length === 0 && (
              <div className="text-center py-12 bg-[#15161C] border border-[#2A2B32] rounded-xl">
                <Briefcase size={40} className="mx-auto text-[#2A2B32] mb-3" />
                <p className="text-[#A0AAB2]">
                  {emp?.role === "manager" ? "No jobs in the system yet." : "No jobs assigned to you yet."}
                </p>
                <p className="text-[#A0AAB2]/60 text-sm mt-1">
                  {emp?.role === "manager" ? "Jobs will appear here as they are created." : "Your assigned jobs will appear here."}
                </p>
              </div>
            )}
          </div>
        )}

        {/* SCHEDULE VIEW */}
        {activeView === "schedule" && (
          <div className="space-y-3" data-testid="emp-schedule-view">
            <h2 className="text-base font-bold text-white mb-2">Upcoming Schedule</h2>
            {schedule.length === 0 ? (
              <div className="text-center py-12 bg-[#15161C] border border-[#2A2B32] rounded-xl">
                <Calendar size={40} className="mx-auto text-[#2A2B32] mb-3" />
                <p className="text-[#A0AAB2]">No upcoming jobs scheduled.</p>
                <p className="text-[#A0AAB2]/60 text-sm mt-1">Scheduled jobs will appear here.</p>
              </div>
            ) : schedule.map(ev => (
              <div key={ev.id} className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-5">
                <h3 className="text-white font-bold">{ev.title}</h3>
                <div className="flex flex-wrap gap-3 text-[#A0AAB2] text-sm mt-2">
                  <span className="flex items-center space-x-1"><Clock size={12} /><span>{ev.date} {ev.time}</span></span>
                  {ev.address && <span className="flex items-center space-x-1"><MapPin size={12} /><span>{ev.address}</span></span>}
                  {ev.client_name && <span>{ev.client_name}</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* EARNINGS VIEW */}
        {activeView === "earnings" && (
          <div className="space-y-4" data-testid="emp-earnings-view">
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-8 text-center">
              <p className="text-[#A0AAB2] text-sm">Total Earnings</p>
              <p className="text-4xl font-bold text-green-400 mt-2" data-testid="emp-total-earnings">
                ${earnings.total_earned.toLocaleString(undefined, {minimumFractionDigits: 2})}
              </p>
            </div>
            <h3 className="text-base font-bold text-white">Per Job Breakdown</h3>
            {earnings.jobs.length === 0 ? (
              <div className="text-center py-8 bg-[#15161C] border border-[#2A2B32] rounded-xl">
                <DollarSign size={40} className="mx-auto text-[#2A2B32] mb-3" />
                <p className="text-[#A0AAB2]">No earnings recorded yet.</p>
                <p className="text-[#A0AAB2]/60 text-sm mt-1">Earnings will appear as jobs are completed and payouts are set.</p>
              </div>
            ) : earnings.jobs.map(j => (
              <div key={j.job_id} className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4 flex items-center justify-between">
                <div>
                  <h4 className="text-white font-medium text-sm">{j.job_title}</h4>
                  <p className="text-[#A0AAB2] text-xs">{j.client_name} — {j.scheduled_date}</p>
                </div>
                <span className="text-green-400 font-bold">${(j.payout || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeePortalPage;
